import { useEffect, useRef, type ReactNode } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Alert,
  Box,
  Button,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { Palette } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  useNavigate,
  useParams,
  useResolvedPath,
  useSearchParams,
} from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import { dayjs } from "@thalamos/common";
import {
  ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
  AssessmentIdSchema,
  showsInSummary,
  type FinishedAssessment,
  type Person,
  type PersonInvolvement,
} from "@thalamos/haven-common";

import { apiHooks } from "../../api/index.js";
import { PersonBanner } from "../../components/PersonBanner/PersonBanner.js";
import {
  PROGRESSION_COLUMNS,
  ProgressionChart,
  ProgressionLegend,
  type ProgressionPoint,
} from "../../components/ProgressionChart/ProgressionChart.js";
import {
  RatingBadge,
  useAnswerWords,
} from "../../components/RatingBadge/RatingBadge.js";
import { useUser } from "../../Context/UserContext.js";
import {
  createRoute,
  HISTORY_PARAMETERS,
  type HistoryView,
} from "../../routing/createRoute.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import { SHORT_DATE } from "../Assessment/AssessmentViewHeader.js";
import { familyOfName } from "../Assessment/comparisonCandidates.js";
import {
  ratedOfReading,
  useAssessmentReadings,
} from "../Assessment/useAssessmentReadings.js";
import {
  assessorNameOf,
  finishedAssessmentActions,
} from "../Persons/involvementManagement.js";
import {
  approvedOf,
  countsOf,
  dayOf,
  eventsOf,
  familiesOf,
  HISTORY_ITEMS,
  rangeOf,
  rowsOf,
  WINDOW_SIZE,
  windowOf,
  withinRange,
  yearBefore,
  type AcrossRange,
  type HistoryEvent,
  type HistoryFamily,
  type HistoryItems,
  type HistoryPoint,
  type HistoryRow,
  type HistoryRowGroup,
} from "./fullHistory.js";

/** What the page shows beneath its controls: the history, or why there is none. */
export type HistoryBody =
  | { kind: "loading" }
  | { kind: "unreadable" }
  | { kind: "none" }
  | { kind: "none-in-range" }
  | {
      kind: "none-laid-out";
      /** How many are in the range: none could be read or laid out, so all are left out. */
      notShown: number;
    }
  | {
      kind: "shown";
      /** Oldest first. */
      points: ProgressionPoint[];
      events: HistoryEvent[];
      groups: HistoryRowGroup[];
      /** How many in the range could not be read or laid out, and are left out. */
      notShown: number;
    };

/** Where the page's way back leads. */
export type HistoryBack =
  | { kind: "assessment"; href: string; at: Date | undefined }
  | { kind: "person"; href: string };

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const dayParameter = (value: string | null): string | undefined =>
  value !== null && DAY.test(value) ? value : undefined;

const itemsOf = (value: string | null): HistoryItems =>
  HISTORY_ITEMS.find((items) => items === value) ?? "current";

/** The view an address names, each part it leaves out or garbles at its default. */
export const historyViewOf = (
  query: URLSearchParams,
): HistoryView & { items: HistoryItems } => ({
  family: query.get(HISTORY_PARAMETERS.family) ?? undefined,
  from: dayParameter(query.get(HISTORY_PARAMETERS.from)),
  to: dayParameter(query.get(HISTORY_PARAMETERS.to)),
  items: itemsOf(query.get(HISTORY_PARAMETERS.items)),
  window: dayParameter(query.get(HISTORY_PARAMETERS.window)),
  back: AssessmentIdSchema.safeParse(query.get(HISTORY_PARAMETERS.back)).data,
  item: query.get(HISTORY_PARAMETERS.item) ?? undefined,
});

const dateOfDay = (day: string) => SHORT_DATE.format(dayjs(day).toDate());

const MONTH = new Intl.DateTimeFormat("en-GB", { month: "short" });
const DAY_AND_MONTH = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
});
const MONTH_AND_YEAR = new Intl.DateTimeFormat("en-GB", {
  month: "short",
  year: "numeric",
});

/** `changed` is muted: the item moved without coming to call for action, or it is history, which never does. */
const changeToneByAcrossRange: Record<AcrossRange, keyof Palette["change"]> = {
  not_rated: "blank",
  same: "muted",
  changed: "muted",
  persistent: "rust",
  new: "warm",
  recurring: "warm",
  still_to_build: "cool",
  resolved: "cool",
  built: "cool",
};

const OUTLINED = {
  textTransform: "none",
  whiteSpace: "nowrap",
  flexShrink: 0,
  color: "text.primary",
  borderColor: "divider",
  fontWeight: 600,
} as const;

const TOGGLE = {
  textTransform: "none",
  py: 0.25,
  fontSize: "0.8125rem",
  fontWeight: 600,
  color: "text.primary",
  "&.Mui-selected, &.Mui-selected:hover": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
  },
} as const;

const HEADING = {
  fontSize: "0.6875rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
} as const;

/**
 * What heads each assessment's column: its month, its day too where another
 * column shares the month, and its year on the first column and wherever the
 * year changes.
 */
const columnHeadingsOf = (points: readonly ProgressionPoint[]) =>
  points.map((point, index) => {
    const before = points[index - 1];
    const month = MONTH_AND_YEAR.format(point.at);
    const shared = points.some(
      (other) => other !== point && MONTH_AND_YEAR.format(other.at) === month,
    );

    return {
      point,
      year:
        before === undefined ||
        before.at.getFullYear() !== point.at.getFullYear()
          ? String(point.at.getFullYear())
          : undefined,
      label: shared ? DAY_AND_MONTH.format(point.at) : MONTH.format(point.at),
    };
  });

/** One rating in the table: a badge where the summary shows it, written plainly where it does not, a dot where none was given. */
const RatingCell = ({
  row,
  rating,
}: {
  row: HistoryRow;
  rating: number | undefined;
}) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();
  const spoken = (): string => {
    if (rating === undefined) {
      return t("assessment.history.notRated");
    }

    return match(row.scale)
      .with("yes_no", (scale) => wordOf({ scale, rating }))
      .with("rating", () => t("assessment.history.rated", { rating }))
      .exhaustive();
  };
  const drawn = (): ReactNode => {
    if (rating === undefined) {
      return "·";
    }

    return showsInSummary({ category: row.category, rating }) ? (
      <Box sx={{ display: "inline-flex" }}>
        <RatingBadge
          category={row.category}
          rating={rating}
          scale={row.scale}
          size="small"
        />
      </Box>
    ) : (
      wordOf({ scale: row.scale, rating })
    );
  };

  return (
    <TableCell
      align="center"
      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
    >
      <Box component="span" aria-hidden>
        {drawn()}
      </Box>
      <Box component="span" sx={VISUALLY_HIDDEN}>
        {spoken()}
      </Box>
    </TableCell>
  );
};

const HistoryTable = ({
  points,
  groups,
  window,
  pickedItem,
}: {
  points: readonly ProgressionPoint[];
  groups: readonly HistoryRowGroup[];
  window: { from: number; to: number };
  pickedItem: string | undefined;
}) => {
  const { t } = useTranslation();
  const picked = useRef<HTMLTableRowElement>(null);
  const shown = points.slice(window.from, window.to + 1);

  useEffect(() => {
    picked.current?.scrollIntoView({ block: "center" });
  }, []);

  return (
    <TableContainer>
      <Table
        size="small"
        aria-label={t("assessment.history.table.label")}
        sx={{
          tableLayout: "fixed",
          "& td, & th": { borderColor: "divider" },
        }}
      >
        <colgroup>
          <col style={{ width: PROGRESSION_COLUMNS.item }} />
          {shown.map((point) => (
            <col key={point.assessmentId} />
          ))}
          <col style={{ width: PROGRESSION_COLUMNS.across }} />
        </colgroup>
        <TableHead>
          <TableRow
            sx={{ "& th": { borderBottom: 2, borderColor: "text.primary" } }}
          >
            <TableCell scope="col" sx={{ ...HEADING, verticalAlign: "bottom" }}>
              {t("assessment.history.table.item")}
            </TableCell>
            {columnHeadingsOf(shown).map(({ point, year, label }) => (
              <TableCell
                key={point.assessmentId}
                scope="col"
                align="center"
                sx={{ verticalAlign: "bottom", px: 0.5 }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{ display: "block", color: "primary.main" }}
                >
                  {year === undefined ? null : (
                    <Box
                      component="span"
                      sx={{
                        display: "block",
                        fontSize: "0.625rem",
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      {year}
                    </Box>
                  )}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {label}
                  </Box>
                </Box>
                <Box component="span" sx={VISUALLY_HIDDEN}>
                  {SHORT_DATE.format(point.at)}
                </Box>
              </TableCell>
            ))}
            <TableCell scope="col" sx={{ ...HEADING, verticalAlign: "bottom" }}>
              {t("assessment.history.table.across")}
            </TableCell>
          </TableRow>
        </TableHead>
        {groups.map((group) => (
          <TableBody key={group.key}>
            <TableRow>
              <TableCell
                component="th"
                scope="colgroup"
                colSpan={shown.length + 2}
                sx={{ pt: 1.5 }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {t(`assessment.history.group.${group.category}`, {
                    domain: group.domain,
                  })}
                </Box>
                <Box
                  component="span"
                  sx={{ ml: 1, fontSize: "0.75rem", color: "text.secondary" }}
                >
                  {t("assessment.history.groupItems", {
                    count: group.rows.length,
                  })}
                </Box>
              </TableCell>
            </TableRow>
            {group.rows.map((row) => {
              const isPicked = row.item === pickedItem;
              const { across } = row;

              return (
                <TableRow
                  key={row.item}
                  ref={isPicked ? picked : undefined}
                  aria-current={isPicked ? "true" : undefined}
                  selected={isPicked}
                >
                  <TableCell component="th" scope="row">
                    {row.title}
                  </TableCell>
                  {shown.map((point, index) => (
                    <RatingCell
                      key={point.assessmentId}
                      row={row}
                      rating={row.ratings[window.from + index]}
                    />
                  ))}
                  <TableCell>
                    {across === undefined ? null : (
                      <Box
                        component="span"
                        sx={(theme) => {
                          const swatch =
                            theme.palette.change[
                              changeToneByAcrossRange[across]
                            ];

                          return {
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 1,
                            fontSize: "0.6875rem",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            bgcolor: swatch.background,
                            color: swatch.color,
                          };
                        }}
                      >
                        {t(`assessment.history.across.${across}`)}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        ))}
      </Table>
    </TableContainer>
  );
};

const WindowControl = ({
  points,
  window,
  onWindow,
}: {
  points: readonly ProgressionPoint[];
  window: { from: number; to: number };
  /** Asks for the chart and the table to end at the point at this index. */
  onWindow: (end: number) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const first = points[window.from];
  const last = points[window.to];

  if (
    points.length <= WINDOW_SIZE ||
    first === undefined ||
    last === undefined
  ) {
    return null;
  }

  const showing = t("assessment.history.window.showing", {
    from: MONTH_AND_YEAR.format(first.at),
    to: MONTH_AND_YEAR.format(last.at),
  });

  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Button
        size="small"
        variant="outlined"
        disabled={window.from === 0}
        onClick={() => void onWindow(window.to - 1)}
        sx={OUTLINED}
      >
        {t("assessment.history.window.earlier")}
      </Button>
      <Slider
        size="small"
        min={WINDOW_SIZE - 1}
        max={points.length - 1}
        step={1}
        value={window.to}
        aria-label={t("assessment.history.window.label")}
        getAriaValueText={() => showing}
        onChange={(_, value) => {
          if (typeof value === "number") {
            void onWindow(value);
          }
        }}
        sx={{ flexGrow: 1 }}
      />
      <Button
        size="small"
        variant="outlined"
        disabled={window.to === points.length - 1}
        onClick={() => void onWindow(window.to + 1)}
        sx={OUTLINED}
      >
        {t("assessment.history.window.later")}
      </Button>
      <Typography
        variant="body2"
        sx={{ fontWeight: 600, whiteSpace: "nowrap" }}
      >
        {showing}
      </Typography>
    </Stack>
  );
};

const BodyShown = ({
  body,
  view,
  onView,
}: {
  body: HistoryBody;
  view: HistoryView;
  onView: (view: HistoryView) => Promisable<void>;
}): ReactNode => {
  const { t } = useTranslation();
  const items = view.items ?? "current";

  return match(body)
    .with({ kind: "loading" }, () => (
      <Typography color="text.secondary">
        {t("assessment.history.loading")}
      </Typography>
    ))
    .with({ kind: "unreadable" }, () => (
      <Alert severity="warning">{t("assessment.history.unreadable")}</Alert>
    ))
    .with({ kind: "none" }, () => (
      <Alert severity="info">{t("assessment.history.none")}</Alert>
    ))
    .with({ kind: "none-in-range" }, () => (
      <Alert severity="info">{t("assessment.history.noneInRange")}</Alert>
    ))
    .with({ kind: "none-laid-out" }, ({ notShown }) => (
      <Alert severity="warning">
        {t("assessment.history.noneLaidOut", { count: notShown })}
      </Alert>
    ))
    .with({ kind: "shown" }, ({ points, events, groups, notShown }) => {
      const days = points.map(({ at }) => dayOf(at));
      const window = windowOf({ days, endingOn: view.window });
      const latest = points.at(-1);

      return (
        <Stack spacing={2}>
          {notShown === 0 ? null : (
            <Alert severity="warning">
              {t("assessment.history.notShown", { count: notShown })}
            </Alert>
          )}
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, borderColor: "divider" }}
          >
            <Stack spacing={1.5}>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                useFlexGap
                flexWrap="wrap"
              >
                <Box>
                  <Typography
                    component="h2"
                    sx={{ m: 0, fontSize: "1rem", fontWeight: 700 }}
                  >
                    {t("assessment.history.chart.title")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {points.length > WINDOW_SIZE
                      ? t("assessment.history.chart.windowed", {
                          count: points.length,
                          size: WINDOW_SIZE,
                        })
                      : t("assessment.history.chart.allShown", {
                          count: points.length,
                        })}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <ProgressionLegend />
                  <Typography variant="caption" color="text.secondary">
                    {t("assessment.history.chart.explained")}
                  </Typography>
                </Box>
              </Stack>
              <WindowControl
                points={points}
                window={window}
                onWindow={(end) =>
                  onView({
                    ...view,
                    window: end === points.length - 1 ? undefined : days[end],
                  })
                }
              />
              {latest === undefined ? null : (
                <ProgressionChart
                  points={points.slice(window.from, window.to + 1)}
                  events={events}
                  latest={latest.counts}
                />
              )}
              {groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(`assessment.history.nothingListed.${items}`)}
                </Typography>
              ) : (
                <HistoryTable
                  points={points}
                  groups={groups}
                  window={window}
                  pickedItem={view.item}
                />
              )}
            </Stack>
          </Paper>
        </Stack>
      );
    })
    .exhaustive();
};

/**
 * The full history's rendering, with everything it shows injected so a story
 * or a test can show any state of it without an API client.
 */
export const FullHistoryPageContent = ({
  person,
  involvements,
  back,
  families,
  family,
  days,
  view,
  body,
  onView,
}: {
  /** The young person, for the banner; it is left out until read. */
  person?: Person | undefined;
  involvements?: PersonInvolvement[] | undefined;
  back: HistoryBack;
  /** Undefined until the history is read. */
  families: readonly HistoryFamily[] | undefined;
  /** The family shown, where there is one. */
  family: string | undefined;
  /** The days of the family's approved assessments, oldest first. */
  days: readonly string[];
  view: HistoryView;
  body: HistoryBody;
  onView: (view: HistoryView) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const items = view.items ?? "current";
  const first = days[0];
  const last = days.at(-1);
  const range = rangeOf({ days, from: view.from, to: view.to });
  const inRange = days.filter(
    (day) =>
      (range.from === undefined || day >= range.from) &&
      (range.to === undefined || day <= range.to),
  );
  const firstInRange = inRange[0];
  const lastInRange = inRange.at(-1);

  const backLabel = () => {
    if (back.kind === "person") {
      return t("assessment.history.backToPersonPage");
    }

    return back.at === undefined
      ? t("assessment.history.backToAssessment")
      : t("assessment.history.back", { date: SHORT_DATE.format(back.at) });
  };

  const choose = ({
    from,
    to,
  }: {
    from: string | undefined;
    to: string | undefined;
  }) => {
    // The address leaves out an end at the last day and a start a year before
    // the end, those being the range's defaults.
    const end = to === last ? undefined : to;
    const yearStart =
      last === undefined ? undefined : yearBefore({ days, to: end ?? last });

    void onView({
      ...view,
      from: from === yearStart ? undefined : from,
      to: end,
      window: undefined,
    });
  };

  return (
    <Stack spacing={2}>
      {person === undefined ? null : (
        <PersonBanner person={person} involvements={involvements ?? []} />
      )}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          component={RouterLink}
          to={back.href}
          startIcon={<ArrowBackIcon />}
          size="small"
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {backLabel()}
        </Button>
        {family === undefined ? null : (
          <Box
            component="span"
            sx={{
              ...HEADING,
              px: 1,
              py: 0.25,
              borderRadius: 4,
              border: 1,
              borderColor: "primary.main",
              color: "primary.main",
            }}
          >
            {family}
          </Box>
        )}
      </Stack>
      <Box>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Typography
            component="h1"
            sx={{ m: 0, fontSize: "1.375rem", fontWeight: 700 }}
          >
            {t("assessment.history.title")}
          </Typography>
          {families === undefined || families.length === 0 ? null : (
            <>
              <Typography
                id="history-instrument"
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                {t("assessment.history.instrument")}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <Select
                  labelId="history-instrument"
                  value={family ?? ""}
                  sx={{ fontSize: "0.875rem", fontWeight: 600 }}
                  // Another family has other days, so the range, the
                  // table's window and any item picked out start afresh.
                  onChange={(event) =>
                    void onView({
                      family: event.target.value,
                      items: view.items,
                      back: view.back,
                    })
                  }
                >
                  {families.map((each) => (
                    <MenuItem key={each.family} value={each.family}>
                      {t("assessment.history.instrumentOption", {
                        family: each.family,
                        count: each.count,
                        date: SHORT_DATE.format(each.latest),
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {t("assessment.history.instrumentHint")}
              </Typography>
            </>
          )}
        </Stack>
        {firstInRange === undefined || lastInRange === undefined ? null : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("assessment.history.inRange", {
              count: inRange.length,
              from: dateOfDay(firstInRange),
              to: dateOfDay(lastInRange),
            })}
          </Typography>
        )}
      </Box>
      {first === undefined || last === undefined ? null : (
        <Paper
          variant="outlined"
          sx={{ px: 2, py: 1.25, borderRadius: 2, borderColor: "divider" }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
          >
            <Typography
              id="history-from"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {t("assessment.history.from")}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                labelId="history-from"
                value={firstInRange ?? first}
                sx={{ fontSize: "0.875rem" }}
                onChange={(event) => {
                  choose({ from: event.target.value, to: view.to });
                }}
              >
                {days
                  .filter((day) => day <= (view.to ?? last))
                  .map((day) => (
                    <MenuItem key={day} value={day}>
                      {dateOfDay(day)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Typography
              id="history-to"
              variant="body2"
              sx={{ fontWeight: 700 }}
            >
              {t("assessment.history.to")}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select
                labelId="history-to"
                value={view.to ?? last}
                sx={{ fontSize: "0.875rem" }}
                onChange={(event) => {
                  choose({ from: view.from, to: event.target.value });
                }}
              >
                {days
                  .filter((day) => day >= (firstInRange ?? first))
                  .map((day) => (
                    <MenuItem key={day} value={day}>
                      {dateOfDay(day)}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                choose({ from: undefined, to: view.to });
              }}
              sx={OUTLINED}
            >
              {t("assessment.history.previousYear")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                choose({ from: first, to: undefined });
              }}
              sx={OUTLINED}
            >
              {t("assessment.history.all")}
            </Button>
            <Typography variant="body2" sx={{ fontWeight: 700, ml: 1 }}>
              {t("assessment.history.itemsLabel")}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={items}
              aria-label={t("assessment.history.itemsLabel")}
              onChange={(_, next: HistoryItems | null) => {
                // An exclusive group reports null for a click on the chosen
                // button, which would leave nothing chosen.
                if (next !== null) {
                  void onView({ ...view, items: next });
                }
              }}
            >
              {HISTORY_ITEMS.map((each) => (
                <ToggleButton key={each} value={each} sx={TOGGLE}>
                  {t(`assessment.history.items.${each}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Paper>
      )}
      <BodyShown body={body} view={view} onView={onView} />
    </Stack>
  );
};

/**
 * How a young person's approved assessments on one family of instruments have
 * gone, at an address of its own that holds the family, the range, which
 * items are listed and which assessments the chart and table show.
 */
export const FullHistoryPage = () => {
  const { personId } = useParams();
  // `useParams` types every segment as optional, but the route cannot match
  // without this one.
  const requestedId = personId ?? "";
  // The person page this history hangs off, read from the address so the way
  // back is there before, or without, the record.
  const personPageHref = useResolvedPath("..", { relative: "path" }).pathname;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const view = historyViewOf(searchParams);
  const { user } = useUser();
  const [{ data: person, response: personResponse }] =
    apiHooks.person.byId(requestedId);
  // A 4xx body resolves onto `data`, so each is only taken from a 200.
  const read = personResponse?.status === 200 ? person : undefined;
  const [{ data: involvements, response: involvementsResponse }] =
    apiHooks.involvement.forPerson(requestedId);
  const known = involvementsResponse?.status === 200 ? involvements : undefined;
  const [{ data: history, response: historyResponse }] =
    apiHooks.assessment.historyForPerson(requestedId, read, {
      limit: ASSESSMENT_HISTORY_PAGE_LIMIT_MAX,
    });
  const finished =
    historyResponse?.status === 200 ? history?.results : undefined;

  const mayRead = finishedAssessmentActions(user, known, read);
  const approved =
    finished === undefined || read === undefined
      ? undefined
      : approvedOf({
          history: finished,
          mayRead: (assessment) => mayRead(assessment).view,
        });
  const families = approved === undefined ? undefined : familiesOf(approved);
  // A family the address names that `familiesOf` does not offer, as from a
  // stale link, gives way to the first it does.
  const family =
    families?.find((each) => each.family === view.family)?.family ??
    families?.[0]?.family;
  const ofFamily = (approved ?? []).filter(
    ({ instrument }) => familyOfName(instrument.name) === family,
  );
  const days = ofFamily.map(({ finishedAt }) => dayOf(finishedAt));
  const inRange = withinRange({
    assessments: ofFamily,
    ...rangeOf({ days, from: view.from, to: view.to }),
  });
  const readings = useAssessmentReadings(inRange.map(({ id }) => id));

  const pointOf = (row: FinishedAssessment): HistoryPoint | undefined => {
    const rated = ratedOfReading(readings.get(row.id));

    return rated === undefined
      ? undefined
      : {
          assessmentId: row.id,
          at: row.finishedAt,
          rated,
          assessor: assessorNameOf(row, user, known),
        };
  };

  const body = (): HistoryBody => {
    // Who may read what is read off the involvements, so the history waits
    // for them as well as for the record and the history itself.
    if (finished === undefined || read === undefined || known === undefined) {
      const refused = [
        personResponse,
        involvementsResponse,
        historyResponse,
      ].some((response) => response !== undefined && response.status !== 200);

      return refused ? { kind: "unreadable" } : { kind: "loading" };
    }

    if (ofFamily.length === 0) {
      return { kind: "none" };
    }

    if (inRange.length === 0) {
      return { kind: "none-in-range" };
    }

    if (inRange.some(({ id }) => !readings.has(id))) {
      return { kind: "loading" };
    }

    const points = inRange.flatMap((row) => pointOf(row) ?? []);

    return points.length === 0
      ? { kind: "none-laid-out", notShown: inRange.length }
      : {
          kind: "shown",
          points: points.map((point) => ({
            assessmentId: point.assessmentId,
            at: point.at,
            counts: countsOf(point.rated),
          })),
          events: eventsOf({ points, involvements: known }),
          groups: rowsOf({ points, items: view.items }),
          notShown: inRange.length - points.length,
        };
  };

  const backTo = finished?.find(({ id }) => id === view.back);
  // The record the address names, which may be one merged into `read`, so a
  // change of view keeps naming the record the page was asked for.
  const namedRecordId =
    read === undefined
      ? undefined
      : (read.mergedPersonRecords.find((merged) => merged.id === requestedId)
          ?.id ?? read.id);

  return (
    <FullHistoryPageContent
      person={read}
      involvements={known}
      back={
        view.back === undefined
          ? { kind: "person", href: personPageHref }
          : {
              kind: "assessment",
              href: createRoute.assessmentSummary(view.back),
              at: backTo?.finishedAt,
            }
      }
      families={families}
      family={family}
      days={days}
      view={view}
      body={body()}
      onView={(next) => {
        // Unreachable: every control that changes the view is drawn from data
        // that waits for `read`, which `namedRecordId` is defined with. Kept to
        // fail loudly if not.
        /* v8 ignore if */
        if (namedRecordId === undefined) {
          throw new Error(
            "Expected a view to be chosen only once the record is read",
          );
        }

        // A change of view replaces the entry: it is the same page.
        navigate(createRoute.fullHistory(namedRecordId, next), {
          replace: true,
        });
      }}
    />
  );
};
